import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatEventTimestamp } from '@klareospace/pharos-lib-front-utils';
import type { HistoryEntry, Order } from '@klareospace/pharos-lib-types';
import { ErrorBanner, LifecycleTimeline, Skeleton } from '@klareospace/pharos-lib-web-utils';

import { mapLifecycleSlots, toLifecycleSteps } from '../utils/lifecycle-mapper';
import { translateStatusReason } from '../utils/status-reason';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LifecycleSectionProps {
  readonly history: HistoryEntry[] | undefined;
  readonly isError: boolean;
  readonly isLoading: boolean;
  readonly order: Order;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function LifecycleSection({ history, isError, isLoading, order }: LifecycleSectionProps): React.JSX.Element {
  const { t } = useTranslation('orders');

  // Memoize steps before early returns to satisfy hook ordering rules.
  // Returns [] when history is undefined (skeleton / loading / error path).
  const steps = useMemo(() => {
    if (history === undefined) return [];
    const rawSlots = mapLifecycleSlots(order.status, history);
    const slots = rawSlots.map((slot) => ({
      ...slot,
      reason: translateStatusReason(t, slot.reason),
    }));
    const formatConnectorLabel = (count: number, kind: 'done' | 'upcoming'): string =>
      kind === 'done' ? t('lifecycle.connectorDone', { count }) : t('lifecycle.connectorUpcoming', { count });
    return toLifecycleSteps(slots, (s) => t(`status.${s}`), formatEventTimestamp, formatConnectorLabel);
  }, [order.status, history, t]);

  if (isError) {
    return <ErrorBanner message={t('history.error')} />;
  }

  if (isLoading || history === undefined) {
    return <Skeleton className="h-20 w-full rounded-lg" />;
  }

  return (
    <LifecycleTimeline
      scrollLeftLabel={t('lifecycle.scrollLeft')}
      scrollRightLabel={t('lifecycle.scrollRight')}
      steps={steps}
    />
  );
}
